import { Injectable } from '@angular/core';
import { InternalDatabaseService } from '../internal/internal-database.service';
import { WhoChecklistStorage } from './who-checklist-storage';
import { Observable, BehaviorSubject, from } from 'rxjs';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';
import { WhoChecklist } from '@stockcare-models/WhoChecklist';

@Injectable({
  providedIn: 'root',
})
export class WhoChecklistStorageService {
  readonly table: string;
  readonly Index: string;
  enableLocaldb: boolean;
  whoChecklists: WhoChecklistStorage[] = [];

  private checklistUpdated = new BehaviorSubject<{ patientTheatreVisitId: number; checklist?: WhoChecklist }>({ patientTheatreVisitId: -1 });
  public checklistUpdated$ = this.checklistUpdated.asObservable();

  constructor(
    private internalDatabaseService: InternalDatabaseService,
    private readonly settingsService: SettingsService
  ) {
    const table = WhoChecklistStorage.getTableDefinition();
    this.table = table.TableName;
    this.Index = table.Indexes[0].Name;
  }

  getSettings() {
    this.enableLocaldb = this.settingsService.getBooleanSetting(Settings.ENABLE_INDEXED_DB);
  }

  getWhoChecklistById(patientTheatreVisitId: number): Observable<WhoChecklist | undefined> {
    this.getSettings();

    return new Observable<WhoChecklist | undefined>((observer) => {
      if (!this.enableLocaldb) {
        const storage = this.whoChecklists.find(c => c.patientTheatreVisitId === patientTheatreVisitId);
        const result = storage ? this.mapFromStorage(storage) : undefined;
        observer.next(result);
        observer.complete();
        return;
      }

      this.internalDatabaseService.getByIndex<WhoChecklistStorage>(
        this.table,
        this.Index,
        patientTheatreVisitId
      ).then((storage) => {
        if (storage) {
          const whoChecklist = this.mapFromStorage(storage);
          observer.next(whoChecklist);
        } else {
          observer.next(undefined);
        }
        observer.complete();
      }).catch((error) => {
        console.error('[WhoChecklistStorageService] Error getting checklist from IndexedDB:', error);
        observer.next(undefined);
        observer.complete();
      });
    });
  }

  updateWhoChecklist(whoChecklist: WhoChecklist): Observable<boolean> {
    this.getSettings();

    return new Observable<boolean>((observer) => {
      try {
        const storage = this.mapToStorage(whoChecklist);
        
        if (!this.enableLocaldb) {
          const index = this.whoChecklists.findIndex(c => c.patientTheatreVisitId === storage.patientTheatreVisitId);
          if (index !== -1) {
            this.whoChecklists[index] = storage;
          } else {
            this.whoChecklists.push(storage);
          }

          this.checklistUpdated.next({
            patientTheatreVisitId: storage.patientTheatreVisitId,
            checklist: whoChecklist
          });

          observer.next(true);
          observer.complete();
          return;
        }

        // Handle IndexedDB operations
        this.internalDatabaseService.getByIndex<WhoChecklistStorage>(
          this.table,
          this.Index,
          storage.patientTheatreVisitId
        ).then(async (existingChecklist) => {
          try {
            if (existingChecklist) {
              await this.internalDatabaseService.update(this.table, storage);
            } else {
              await this.internalDatabaseService.add(this.table, storage);
            }

            this.checklistUpdated.next({
              patientTheatreVisitId: storage.patientTheatreVisitId,
              checklist: whoChecklist
            });

            observer.next(true);
            observer.complete();
          } catch (updateError) {
            console.error('[WhoChecklistStorageService] Error updating checklist:', updateError);
            observer.error(updateError);
          }
        }).catch((error) => {
          console.error('[WhoChecklistStorageService] Error checking existing checklist:', error);
          observer.error(error);
        });

      } catch (error) {
        console.error('[WhoChecklistStorageService] Error in updateWhoChecklist:', error);
        observer.error(error);
      }
    });
  }

  /**
   * Map WhoChecklist API model to WhoChecklistStorage storage model
   */
  private mapToStorage(whoChecklist: WhoChecklist): WhoChecklistStorage {
    const storage = new WhoChecklistStorage();
    storage.patientTheatreVisitId = whoChecklist.patientTheatreVisitId;
    
    // Serialize each option field
    storage.q6Options = whoChecklist.q6Options ? JSON.stringify(whoChecklist.q6Options) : undefined;
    storage.q6aOptions = whoChecklist.q6aOptions ? JSON.stringify(whoChecklist.q6aOptions) : undefined;
    storage.q7Options = whoChecklist.q7Options ? JSON.stringify(whoChecklist.q7Options) : undefined;
    storage.q8Options = whoChecklist.q8Options ? JSON.stringify(whoChecklist.q8Options) : undefined;
    storage.q9Options = whoChecklist.q9Options ? JSON.stringify(whoChecklist.q9Options) : undefined;
    storage.q10Options = whoChecklist.q10Options ? JSON.stringify(whoChecklist.q10Options) : undefined;
    storage.q11Options = whoChecklist.q11Options ? JSON.stringify(whoChecklist.q11Options) : undefined;
    storage.q12Options = whoChecklist.q12Options ? JSON.stringify(whoChecklist.q12Options) : undefined;
    storage.q13Options = whoChecklist.q13Options ? JSON.stringify(whoChecklist.q13Options) : undefined;
    storage.q14Options = whoChecklist.q14Options ? JSON.stringify(whoChecklist.q14Options) : undefined;
    storage.q15Options = whoChecklist.q15Options ? JSON.stringify(whoChecklist.q15Options) : undefined;
    storage.q16Options = whoChecklist.q16Options ? JSON.stringify(whoChecklist.q16Options) : undefined;
    storage.q17Options = whoChecklist.q17Options ? JSON.stringify(whoChecklist.q17Options) : undefined;
    storage.q18Options = whoChecklist.q18Options ? JSON.stringify(whoChecklist.q18Options) : undefined;
    storage.q19Options = whoChecklist.q19Options ? JSON.stringify(whoChecklist.q19Options) : undefined;
    storage.q20Options = whoChecklist.q20Options ? JSON.stringify(whoChecklist.q20Options) : undefined;
    storage.q21Options = whoChecklist.q21Options ? JSON.stringify(whoChecklist.q21Options) : undefined;
    storage.q22Options = whoChecklist.q22Options ? JSON.stringify(whoChecklist.q22Options) : undefined;
    storage.q23Options = whoChecklist.q23Options ? JSON.stringify(whoChecklist.q23Options) : undefined;
    storage.q24Options = whoChecklist.q24Options ? JSON.stringify(whoChecklist.q24Options) : undefined;
    storage.q25Options = whoChecklist.q25Options ? JSON.stringify(whoChecklist.q25Options) : undefined;
    storage.q26Options = whoChecklist.q26Options ? JSON.stringify(whoChecklist.q26Options) : undefined;
    storage.q27Options = whoChecklist.q27Options ? JSON.stringify(whoChecklist.q27Options) : undefined;
    storage.q28Options = whoChecklist.q28Options ? JSON.stringify(whoChecklist.q28Options) : undefined;
    
    // Set metadata
    storage.lastUpdated = new Date();
    storage.syncedWithApi = true;

    return storage;
  }

  /**
   * Map WhoChecklistStorage storage model back to WhoChecklist API model
   */
  private mapFromStorage(storage: WhoChecklistStorage): WhoChecklist {
    const whoChecklist = new WhoChecklist();
    whoChecklist.patientTheatreVisitId = storage.patientTheatreVisitId;

    try {
      // Deserialize each option field
      whoChecklist.q6Options = storage.q6Options ? JSON.parse(storage.q6Options) : undefined;
      whoChecklist.q6aOptions = storage.q6aOptions ? JSON.parse(storage.q6aOptions) : undefined;
      whoChecklist.q7Options = storage.q7Options ? JSON.parse(storage.q7Options) : undefined;
      whoChecklist.q8Options = storage.q8Options ? JSON.parse(storage.q8Options) : undefined;
      whoChecklist.q9Options = storage.q9Options ? JSON.parse(storage.q9Options) : undefined;
      whoChecklist.q10Options = storage.q10Options ? JSON.parse(storage.q10Options) : undefined;
      whoChecklist.q11Options = storage.q11Options ? JSON.parse(storage.q11Options) : undefined;
      whoChecklist.q12Options = storage.q12Options ? JSON.parse(storage.q12Options) : undefined;
      whoChecklist.q13Options = storage.q13Options ? JSON.parse(storage.q13Options) : undefined;
      whoChecklist.q14Options = storage.q14Options ? JSON.parse(storage.q14Options) : undefined;
      whoChecklist.q15Options = storage.q15Options ? JSON.parse(storage.q15Options) : undefined;
      whoChecklist.q16Options = storage.q16Options ? JSON.parse(storage.q16Options) : undefined;
      whoChecklist.q17Options = storage.q17Options ? JSON.parse(storage.q17Options) : undefined;
      whoChecklist.q18Options = storage.q18Options ? JSON.parse(storage.q18Options) : undefined;
      whoChecklist.q19Options = storage.q19Options ? JSON.parse(storage.q19Options) : undefined;
      whoChecklist.q20Options = storage.q20Options ? JSON.parse(storage.q20Options) : undefined;
      whoChecklist.q21Options = storage.q21Options ? JSON.parse(storage.q21Options) : undefined;
      whoChecklist.q22Options = storage.q22Options ? JSON.parse(storage.q22Options) : undefined;
      whoChecklist.q23Options = storage.q23Options ? JSON.parse(storage.q23Options) : undefined;
      whoChecklist.q24Options = storage.q24Options ? JSON.parse(storage.q24Options) : undefined;
      whoChecklist.q25Options = storage.q25Options ? JSON.parse(storage.q25Options) : undefined;
      whoChecklist.q26Options = storage.q26Options ? JSON.parse(storage.q26Options) : undefined;
      whoChecklist.q27Options = storage.q27Options ? JSON.parse(storage.q27Options) : undefined;
      whoChecklist.q28Options = storage.q28Options ? JSON.parse(storage.q28Options) : undefined;

    } catch (error) {
      console.error('[WhoChecklistStorageService] Error parsing stored checklist data:', error);
    }

    return whoChecklist;
  }
}
